import SoftooMap from "../Map";
import Map from "ol/Map";
import { Point, Polygon, MultiPolygon } from "ol/geom";
import Feature from "ol/Feature";
import { altKeyOnly, singleClick, click } from 'ol/events/condition.js';
import { Select, Draw, Modify } from "ol/interaction";
import { intersects } from "ol/extent";
import { L } from "@/common/filters";

import { IModifiedFeature } from "../types";

export class DeleteFeature extends Select {
  private $map: SoftooMap;
  private map: Map;
  private targets: IModifiedFeature[]; // the feature to be moved
  private select:Select;
  private draw:Draw;
  private modify:Modify;
  private polygonSource;
  private polygon: Feature[];
  private layers;
  
  constructor(options) {
    options = options || {};
    let afterDeleted;
    if (options.afterDeleted) {
      afterDeleted = options.afterDeleted;
      delete options.afterDeleted;
    }
    let disallowToDelete;
    if (options.disallowToDelete) {
      disallowToDelete = options.disallowToDelete;
      delete options.disallowToDelete;
    }
    let inPolygon: boolean;
    let polygonStyle, polygonSource;
    if (options.inPolygon===true) {
      inPolygon = true;
      delete options.inPolygon;
    }
    if (options.polygonStyle) {
      polygonStyle = options.polygonStyle;
      delete options.polygonStyle;
    }
    if (options.polygonSource) {
      polygonSource = options.polygonSource;
      delete options.polygonSource;
    }
    let pixelTolerance = 6;
    if (options.pixelTolerance) {
      pixelTolerance = options.pixelTolerance;
      delete options.pixelTolerance
    }
    let clickTolerance = Math.max(pixelTolerance-2, 4);
    
    super(Object.assign({
      wrapX: false,
      condition: (event) => {
        return this.draw ? false : singleClick(event);
      }
    }, options));
    
    this.layers = options.layers;
    
    this.select = <Select>this;
    this.selectFeature_ = (event) => {
      this.selectFeature(event);
    }
    if (afterDeleted) {
      this.afterDeleted = afterDeleted;
    }
    if (disallowToDelete) {
      this.disallowToDelete = disallowToDelete;
    }

    if (inPolygon) {
      options = {
        style: polygonStyle
      }
      if (polygonSource) {
        this.polygonSource = polygonSource,
        options.source = polygonSource;
      } else {
        this.polygon = [];
        options.features = this.polygon;
      }

      this.draw = new Draw(Object.assign({}, options, {
        type: 'Polygon',
        clickTolerance: clickTolerance
      }));
      this.modify = new Modify(Object.assign({}, options, {
        pixelTolerance: pixelTolerance,
        deleteCondition: (event) => {
          return altKeyOnly(event) && (singleClick(event) || click(event));
        }
      }));
      this.startDrawPolygon_ = (event) => {
        this.startDrawPolygon(event);
      }
      this.endDrawPolygon_ = (event) => {
        this.endDrawPolygon(event);
      }
      this.modifyDrawPolygon_ = (event) => {
        this.modifyDrawPolygon(event);
      }
    }
  }

  clearSelectdFeature(feature) {
    if (!!this.select.getOverlay) {
      //ol v5
      this.select.getOverlay().getSource().clear();
    } else {
      //ol v6
      let layer = this.select.getLayer(feature);
      if (layer) {
        layer.getSource().clear();
      }
    }
  }

  private selectFeature_;
  selectFeature(event) {
    if (event.selected && event.selected.length>0) {
      let f = event.selected[0];
      if (this.disallowToDelete(f)) {
        let centroid = this.$map.getFeatureCentroid(f).map;
        let self = this;
        this.$map.showInfoPopup(L('attention'), L('sNotAllowedToDeleteLandDueTo'), {
          style: "max-width:480px",
          position: centroid,
          offset: [0, 0],
          cancelText: L('close'),
          methods: {
            doAction(answer) {
              this.closeMe();
            }
          },
          onClose: () => {
            event.selected.forEach((f) => {
              self.clearSelectdFeature(f);
            });
            self.$map.clearHighlightFeature();
          }
        });
      } else {
        this.doDeleteFeature(f);
      }
    }
  }

  private doDeleteFeature(feature:Feature) {
    let org = this.$map.cloneFeature(feature);
    this.$map.addUndo(feature, 'Delete');
    feature.set('STATE', 'Delete');
    this.targets = [{
      original: org,
      modified: feature
    }];
    this.clearSelectdFeature(feature);
    //this.$map.clearHighlightFeature();
    this.afterDeleted(this.targets);
  }

  private startDrawPolygon_;
  startDrawPolygon(event) {
    if (this.polygonSource) {
      this.polygonSource.clear();
    } else {
      this.polygon = [];
    }
  }

  private endDrawPolygon_;
  endDrawPolygon(event) {
    if (this.layers) {
      this.selectFeaturesInPolygon(event.feature);
    }
  }

  private modifyDrawPolygon_;
  modifyDrawPolygon(event) {
    this.endDrawPolygon(Object.assign(event.mapBrowserEvent, {
      feature: event.features.item(0)
    }));
  }

  selectFeaturesInPolygon(feature:Feature) {
    let polygon = feature.getGeometry();
    let polygonExtent = polygon.getExtent();
    let selected:Feature[] = [];
    let disallowed:Feature[] = [];
    this.layers.forEach((layer) => {
      let features = layer.getSource().getFeatures();
      if (features) {
        features.forEach((feature) => {
          if (feature.get('STATE')!='Delete') {
            let g = feature.getGeometry();
            if (g instanceof Point) {
              // NOTE: point on polygon boundary is not intersected
              if (polygon.intersectsCoordinate(g.getCoordinates())) {
                selected.push(feature);
              }
            } else {
              let featureGeomExtent = g.getExtent();
              if (intersects(polygonExtent, featureGeomExtent)) {
                if (g instanceof Polygon || g instanceof MultiPolygon) {
                  let polygons;
                  if (g instanceof MultiPolygon) {
                    polygons = g.getPolygons();
                  } else {
                    polygons = [g];
                  }
                  polygons.forEach((p) => {
                    let coords = p.getLinearRing(0).getCoordinates();
                    let i, inside = true;
                    for (i=0; i<coords.length; i++) {
                      if (!polygon.intersectsCoordinate(coords[i])) {
                        inside = false;
                        break;
                      }
                    }
                    if (inside) {
                      if (this.disallowToDelete(feature)) {
                        disallowed.push(feature);
                      } else {
                        selected.push(feature);
                      }
                    }
                  });
                }
              }
            }
          }
        });
      }
    });
    let selectedSource = this.select.getOverlay().getSource();
    selectedSource.clear();
    let centroid = this.$map.getFeatureCentroid(feature).map;
    if (selected.length>0) {
      selectedSource.addFeatures(selected);
      let self = this;
      let message = L('sAreYouGoingToDeleteSelectedFeatures');
      if (disallowed.length>0) {
        message += ' (' + L('sNotAllowedToDeleteLandDueTo') + ')';
      }
      this.$map.showInfoPopup(L('attention'), message, {
        style: "max-width:480px",
        position: centroid,
        offset: [0, 0],
        okText: L('yes'),
        cancelText: L('no'),
        methods: {
          doAction(answer) {
            if (answer=='accept') {
              self.deleteFeaturesInPolygon(selected);
            }
            self.$map.clearHighlightFeature();
            self.$map.hideInfoPopup();
          }
        },
        onClose: () => {
          self.clearFeaturesInPolygon();
        }
      });
    } else if (disallowed.length>0) {
      let self = this;
      this.$map.showInfoPopup(L('attention'), L('sNotAllowedToDeleteLandDueTo'), {
        style: "max-width:480px",
        position: centroid,
        offset: [0, 0],
        cancelText: L('close'),
        methods: {
          doAction(answer) {
            this.closeMe();
          }
        },
        onClose: () => {
          this.select.getOverlay().getSource().clear();
          self.$map.clearHighlightFeature();
        }
      });
    }
  }

  deleteFeaturesInPolygon(features) {
    features.forEach((f, index) => {
      this.$map.addUndo(f, 'Delete', index>0);
      f.set('STATE', 'Delete');
    });
    this.clearFeaturesInPolygon();
  }

  clearFeaturesInPolygon() {
    this.select.getOverlay().getSource().clear();
    if (this.polygonSource) {
      this.polygonSource.clear();
    } else {
      this.polygon = null;
    }
  }

  afterDeleted(moved:IModifiedFeature[]) {
    // to be override by application
  }

  disallowToDelete(feature:Feature) : boolean {
    // to be override by application
    return false;
  }

  setMap(map:Map) {
    if (map) {
      this.$map = map['SOFTOO_MAP'];
      this.map = map;
      this.select.on('select', this.selectFeature_);
      if (this.draw) {
        this.draw.on('drawstart', this.startDrawPolygon_);
        this.draw.on('drawend', this.endDrawPolygon_);
        this.modify.on('modifyend', this.modifyDrawPolygon_);
        this.$map.addInteractions([this.draw,this.modify]);
        //this.map.addInteraction(this.draw);
        //this.map.addInteraction(this.modify);
        //this.$map.createSnap();
      }
      super.setMap(map);
    }
  }

  remove() {
    if (this.map) {
      this.targets = null;
      this.select.un('select', this.selectFeature_);
      this.select.getOverlay().getSource().clear();
      if (this.draw) {
        this.$map.hideInfoPopup();
        this.polygon = null;
        if (this.polygonSource) {
          this.polygonSource.clear();
        }
        this.draw.un('drawstart', this.startDrawPolygon_);
        this.draw.un('drawend', this.endDrawPolygon_);
        this.modify.un('modifyend', this.modifyDrawPolygon_);
        this.map.removeInteraction(this.draw);
        this.map.removeInteraction(this.modify);
      }
      this.map.removeInteraction(this);
    }
  }
}